"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@/components/ui/button";
import { Anchor } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/context/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function LoginPage() {
  const router = useRouter();
  const { login, sendVerificationCode, isAuthenticated, isLoading, isLoginLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isEmailSubmitted, setIsEmailSubmitted] = useState(false);

  // Redirect if already authenticated
  if (isAuthenticated) {
    router.push('/admin/home');
    return null;
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      return;
    }
    try {
      const success = await sendVerificationCode(email);
      if (success) {
        setIsEmailSubmitted(true);
      }
    } catch (err: any) {
      setIsEmailSubmitted(false);
    }
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode) {
      return;
    }
    try {
      const success = await login(email, verificationCode);
      if (success) {
        router.push("/admin/home");
      }
    } catch (err: any) {
      // Error is handled by toast in AuthContext
    }
  };

  // Show loading state only for initial auth check
  if (isLoading) {
    return (
      <div className="min-h-screen w-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondaryColor mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-screen flex flex-col md:flex-row bg-white dark:bg-gray-950 dark:text-gray-100">
      {/* Left: Image */}
      <div className="relative w-full h-64 md:w-1/2 md:h-auto">
        <Image
          src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1920&auto=format&fit=crop&q=60"
          alt="Cargo ships on ocean background"
          fill
          priority
          className="object-cover"
          quality={60}
          sizes="(max-width: 768px) 100vw, 50vw"
        />
        <div className="absolute inset-0 bg-black/20 pointer-events-none" />
        {/* Animated Waves (optional, only on desktop) */}
        <div className="hidden md:block absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute bottom-0 left-0 right-0 h-[200px] bg-white/10"
              style={{
                clipPath: `polygon(0 ${100 - i * 20}%, 100% ${100 - i * 20}%, 100% 100%, 0 100%)`,
              }}
              animate={{
                y: [0, -20, 0],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                duration: 5 + i * 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>
        {/* Logo always at top left over image */}
        <div className="absolute top-4 left-4 z-10">
          <Link href="/" className="flex items-center gap-2 cursor-pointer group select-none">
            <Image
              src="/logo.png"
              alt="Shipping Nibees Logo"
              width={72}
              height={72}
              className="h-18 w-18 md:h-20 md:w-20 rounded-full object-cover shadow"
              priority
            />
          </Link>
        </div>
      </div>
      {/* Right: Form */}
      <div className="flex flex-1 items-center justify-center bg-white dark:bg-gray-950 relative z-10 min-h-[calc(100vh-0rem)]">
        <div className="w-full max-w-[90%] sm:max-w-md mx-auto flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl p-4 sm:p-6 md:p-8 w-full border dark:border-gray-700">
            <div className="text-center">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-secondaryColor dark:text-secondaryColor">Welcome Back</h2>
              <p className="mt-1 sm:mt-2 text-gray-600 dark:text-gray-300 text-xs sm:text-sm md:text-base">Sign in to your account</p>
            </div>
            
            <div className="mt-4 sm:mt-6 md:mt-8 space-y-4 sm:space-y-6">
              <AnimatePresence mode="wait">
                {!isEmailSubmitted ? (
                  <motion.form
                    key="email-form"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    onSubmit={handleEmailSubmit}
                    className="space-y-3 sm:space-y-4"
                  >
                    <div>
                      <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                        Email address
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1 block w-full px-3 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-secondaryColor focus:border-secondaryColor text-xs sm:text-sm md:text-base dark:bg-gray-700 dark:text-gray-100 dark:placeholder:text-gray-400"
                        placeholder="Enter your email"
                        disabled={isLoginLoading}
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isLoginLoading}
                      className="w-full bg-secondaryColor hover:bg-secondaryColor/90 text-white text-xs sm:text-sm md:text-base py-2 sm:py-2.5 md:py-3 rounded-md sm:rounded-lg dark:bg-secondaryColor dark:hover:bg-secondaryColor/90"
                    >
                      {isLoginLoading ? "Sending verification code..." : "Continue with Email"}
                    </Button>
                  </motion.form>
                ) : (
                  <motion.form
                    key="verification-form"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    onSubmit={handleVerificationSubmit}
                    className="space-y-3 sm:space-y-4"
                  >
                    <div>
                      <label htmlFor="code" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                        Verification Code
                      </label>
                      <InputOTP
                        id="code"
                        name="code"
                        maxLength={6}
                        value={verificationCode}
                        onChange={setVerificationCode}
                        containerClassName="mt-1 w-full justify-center"
                        disabled={isLoginLoading}
                      >
                        <InputOTPGroup className="justify-center gap-3">
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        We've sent a verification code to {email}
                      </p>
                    </div>
                    <Button
                      type="submit"
                      disabled={isLoginLoading}
                      className="w-full bg-secondaryColor hover:bg-secondaryColor/90 text-white text-xs sm:text-sm md:text-base py-2 sm:py-2.5 md:py-3 rounded-md sm:rounded-lg dark:bg-secondaryColor dark:hover:bg-secondaryColor/90"
                    >
                      {isLoginLoading ? "Verifying..." : "Verify Code"}
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEmailSubmitted(false);
                      }}
                      className="w-full text-xs sm:text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      disabled={isLoginLoading}
                    >
                      Use a different email
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 